import React, { useEffect, useState, useCallback } from 'react'

import Cocoon from 'src/resources/images/cocoon.webp'

import { graphql } from 'src/services/graphql'
import { safelyPopToTop } from 'src/helpers/navigation'
import {
  displayNotification,
  createChannel,
  AndroidImportance,
  AndroidStyle,
} from 'src/helpers/notifee'

import { buildComponent } from 'src/components/factory'
import { useAppearance } from 'src/hooks/use-appearance'
import { useRoute, useNavigation } from '@react-navigation/native'
import { useStore, useStoreState } from 'src/hooks/state'

import * as UI from 'native-base'
import * as Elements from 'src/components/elements'
import * as Semantics from 'src/components/semantics'

export const ScreenDisconnectedSignUpComplete = buildComponent<{
  steps?: number
  complete?: (code: string) => Promise<boolean>
}>()
  .withDefaultProps({ steps: 3, complete: graphql.queries.completeSignup })
  .withStyles({
    container: {
      main: {
        flexGrow: 1,
      },
    },
    title: {
      main: {
        textShadowColor: 'rgba(0, 0, 0, 0.5)',
        textShadowOffset: { width: -1, height: 1 },
        textShadowRadius: 5,
      },
    },
  })
  .withLocale('screens.disconnected.signup-complete.screen')
  .withLifecycle(({ props, locale }) => {
    useAppearance({
      title: locale('title'),
    })

    const Store = useStore()
    const navigation = useNavigation()
    const { confirm } = (useRoute().params ?? {}) as { confirm?: string }
    const { complete } = props
    const [gongAssigned, setGongAssigned] = useState(false)

    // Get available gongs from store
    const availableGongs = useStoreState((store) => store.audio.gongs)

    const assignDefaultGong = useCallback(async () => {
      try {
        // Get the first available gong
        if (availableGongs.length > 0) {
          const defaultGong = availableGongs[0]
          await graphql.mutations.setGong(defaultGong._id)

          // Update user state with the assigned gong
          Store.getActions().profile.updateUser({
            gong: defaultGong,
          })

          // Show notification about assigned gong
          const channelId = await createChannel({
            id: 'gong_assignment',
            name: 'Gong Assignment',
            importance: AndroidImportance.HIGH,
          })

          await displayNotification({
            title: locale('gongAssigned.title'),
            body: locale('gongAssigned.message', {
              gongName: defaultGong.short.fr || defaultGong.slug,
            }),
            android: {
              channelId,
              style: {
                type: AndroidStyle.BIGTEXT,
                text: locale('gongAssigned.message', {
                  gongName: defaultGong.short.fr || defaultGong.slug,
                }),
              },
            },
          })

          setGongAssigned(true)
        }
      } catch (error) {
        console.error('Error assigning default gong:', error)
        // Continue with signup completion even if gong assignment fails
      }
    }, [availableGongs, Store, locale])

    useEffect(() => {
      complete(confirm ?? '')
        .then(async (success) => {
          if (!success) {
            safelyPopToTop(navigation)
          } else {
            // Assign default gong after successful signup completion
            await assignDefaultGong()
          }
        })
        .catch(() => {
          safelyPopToTop(navigation)
        })
    }, [navigation, complete, confirm, assignDefaultGong])

    return {
      gongAssigned,
    }
  })
  .withRender(({ props, styles, locale }) => (
    <UI.ScrollView
      scrollIndicatorInsets={{ right: 1 }}
      padding={5}
      contentContainerStyle={styles.container()}
      mx={0}
    >
      <UI.View
        width="100%"
        maxWidth={['100%', 480]}
        minHeight="100%"
        mx={[0, 'auto']}
      >
        <UI.Text
          color="white"
          fontSize="xl"
          textAlign="center"
          marginBottom={4}
          marginTop={16}
          style={styles.title()}
        >
          {locale('title')}
        </UI.Text>

        <Elements.Steps step={props.steps} steps={props.steps} />

        <UI.Column
          flexGrow={1}
          justifyContent="flex-start"
          paddingX={2}
          marginTop={4}
          marginBottom={4}
        >
          <UI.Text
            textAlign="center"
            fontSize="md"
            color="white"
            style={styles.title()}
          >
            {locale('message')}
          </UI.Text>

          <UI.Box
            marginX="auto"
            marginY={16}
            alignItems="center"
            justifyContent="center"
            style={{ aspectRatio: 5 / 4 }}
          >
            <Semantics.Image
              alt=" "
              accessibilityRole="none"
              accessibilityElementsHidden
              width="100%"
              height="100%"
              source={Cocoon}
              resizeMode="contain"
            />
          </UI.Box>
          <Semantics.LinkButton marginBottom={8} to="/login">
            {locale('logIn')}
          </Semantics.LinkButton>
        </UI.Column>
      </UI.View>
    </UI.ScrollView>
  ))
